/**
 * Collapsing overlapping "we may have missed something" resyncs into one.
 *
 * Unlike a chat thread, the app-wide channel has no event log to read forward
 * from — there is no cursor, because nothing durable records that a cue was
 * accepted or someone arrived. Catching up means re-reading the state those
 * events maintain.
 *
 * That read is expensive (inbox, chats, groups, venue, nearby) and it is asked
 * for from three places at once: returning from the background fires an
 * AppState change, and both realtime sockets reconnect a moment later. Left
 * alone that is three full resyncs per resume, for every user, every time.
 *
 * A resync is idempotent, so collapsing duplicates loses nothing: whichever run
 * happens reads the same server state the others would have.
 */

type Options = {
  /**
   * How long after a completed resync a new request is ignored. Long enough to
   * cover the spread between a resume and the reconnects that follow it, short
   * enough that a genuine second trigger is not swallowed.
   */
  minIntervalMs?: number;
  /** Injectable for tests. */
  now?: () => number;
};

export type ResyncGate = {
  /** Ask for a resync. May be ignored if one just ran or is still running. */
  request: () => void;
  /** Whether a resync is running right now. Exposed for tests. */
  isRunning: () => boolean;
};

const DEFAULT_MIN_INTERVAL_MS = 3_000;

export function createResyncGate(
  run: () => Promise<unknown>,
  options: Options = {},
): ResyncGate {
  const minIntervalMs = options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;
  const now = options.now ?? Date.now;

  let running = false;
  let lastFinishedAt: number | null = null;

  return {
    isRunning: () => running,
    request: () => {
      // A run already in flight was started after whatever prompted this
      // request, so it will read the same state. Nothing to add.
      if (running) return;
      if (lastFinishedAt !== null && now() - lastFinishedAt < minIntervalMs) {
        return;
      }

      running = true;
      // Failures are swallowed deliberately: this runs in response to a socket
      // or lifecycle event, not to anything the user did, and each store already
      // records and renders its own failure state. An unowned rejection would
      // surface as an error on top of whatever they are looking at.
      void run()
        .catch(() => {})
        .finally(() => {
          running = false;
          // Timed from completion, not from the start, so a slow resync cannot
          // be immediately followed by another.
          lastFinishedAt = now();
        });
    },
  };
}
